<template>
  <div class="full-width">
    <BreadCrumbs :linkList="linkList" :show-action-links="false"></BreadCrumbs>
    <div class="pvd-grid-root full-width top-half">
      <div class="title-section">
        <pvd-title>
          <h1 slot="heading" class="pvd-title__heading pvd-title__heading--level-1">
            {{ clientConstants.SCREENER_TITLE }}
          </h1>
        </pvd-title>
      </div>
      <div class="pvd-grid__grid">
        <div
          class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-6-at-768 pvd-grid__item--column-span-4-at-960 pvd-grid__item--column-span-4-at-1280">
          <div class="amount-section">
            <pvd-field-group pvd-fixed-width="true">
              <pvd-label id="amount-label" data-cy="amount-field-label" pvd-for="investment-amount-input">
                <span class="regular-font">
                  {{ clientConstants.SCREENER_INPUT_TITLE }}
                </span>
              </pvd-label>
              <apex-currency
                data-cy="amount-field-input"
                label=""
                id-prefix="investment-amount"
                :error="amountValueInvalid"
                :value="formattedAmount"
                @change="amountChange"></apex-currency>
              <pvd-inline-alert
                id="amount-input-alert"
                pvd-size="small"
                v-show="amountValueInvalid"
                data-cy="amount-input-alert">
                {{ clientConstants.AMOUNT_CONTENT_INPUT_ERROR }}
              </pvd-inline-alert>
              <pvd-inline-alert
                id="amount-max-alert"
                pvd-size="small"
                v-show="amountMaxExceeded"
                data-cy="amount-max-alert">
                {{ clientConstants.AMOUNT_CONTENT_MAX_LIMIT_ERROR }}
              </pvd-inline-alert>
            </pvd-field-group>
            <div class="fdic-link">
              <pvd-link id="fdic-link" pvd-trigger @click="openModal()">
                {{ clientConstants.FDIC_LINK }}
              </pvd-link>
            </div>
            <FdicModal ref="fdicModal" />
          </div>
        </div>
        <div
          class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-6-at-768 pvd-grid__item--column-span-4-at-960 pvd-grid__item--column-span-4-at-1280">
          <div id="time-period-slider-component" data-cy="time_period">
            <fractional-cd-slider
              :id="`time-period-slider`"
              :values="clientConstants.timePeriodList"
              :currentValue="durationIndex"
              @change="durationChange"></fractional-cd-slider>
          </div>
        </div>
        <div
          class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-12-at-768 pvd-grid__item--column-span-4-at-960 pvd-grid__item--column-span-4-at-1280">
          <div v-if="this.store.offeringsLoaded" id="gs-timestamp-message" class="gs-timestamp">
            <pvd-microcopy>{{ timestampMessage }}</pvd-microcopy>
          </div>
        </div>
      </div>
    </div>

    <!-- CARD SECTION -->
    <div class="full-width bottom-half">
      <div class="pvd-grid-root">
        <div v-if="this.offerings.length > 0" class="pvd-grid__grid card-section">
          <div class="pvd-grid__item pvd-grid__item--column-span-12">
            <p id="card-section-title-id" class="card-section-title">{{ cardSectionTitle }}</p>
          </div>

          <!-- OPTION 1 or ONLY OPTION -->
          <div class="pvd-grid__item pvd-grid__item--column-span-12">
            <Transition appear name="fade" mode="out-in">
              <p id="option1-text-id" :key="animationTrigger" class="option-text">{{ option1Text }}</p>
            </Transition>
          </div>
          <div
            class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-12-at-768 pvd-grid__item--column-span-8-at-960 pvd-grid__item--column-span-8-at-1280">
            <Transition appear name="fade" mode="out-in">
              <ScreenerCard
                idPrefix="top"
                :cardStyle="topCard.style"
                :cusip="topCard.cusip"
                :duration="topCard.duration"
                :apy="topCard.apy"
                :returnAmount="this.formatDollarAmount(topCard.returnAmount)"
                :investmentAmount="topCard.investmentAmount"
                :title="topCard.title"
                :showFractionalIcon="isTopCardFractionalCD"
                :key="animationTrigger"
                :searchURL="topCard.searchURL"
                data-cy="screener-card-top" />
            </Transition>
          </div>
          <div
            data-cy="top-bullets-container"
            class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-12-at-768 pvd-grid__item--column-span-4-at-960 pvd-grid__item--column-span-4-at-1280">
            <Transition appear name="considerations-fade" mode="out-in">
              <div :key="animationTrigger">
                <ul v-if="isOptionAvailable" class="side-bullets">
                  <li v-html="considerations"></li>
                  <li v-html="totalReturn"></li>
                  <li v-html="oneTransaction"></li>
                  <li v-html="oneDate"></li>
                  <li v-if="isTopCardWholeCD" v-html="mayBeCallable"></li>
                </ul>
                <ul v-if="isOptionAvailable && isFractionalToWholeOpportunity" class="side-bullets top-margin">
                  <li v-html="searchTips"></li>
                  <li v-html="fractionalToWholeTip"></li>
                </ul>
                <ul v-if="!isOptionAvailable" class="side-bullets">
                  <li v-html="searchTips"></li>
                  <li v-if="isIncreaseOpportunity" v-html="increaseInvestmentTip"></li>
                  <li v-if="isDecreaseOpportunity" v-html="decreaseInvestmentTip"></li>
                  <li v-html="changeDurationTip"></li>
                </ul>
              </div>
            </Transition>
          </div>

          <!-- OPTION 2 - MIXED LOT -->
          <div v-show="isMixedOpportunity" class="pvd-grid__item pvd-grid__item--column-span-12">
            <Transition appear name="fade-bottom" mode="out-in">
              <p id="option2-text-id" :key="animationTrigger" class="option-2 option-text">
                {{ option2Text }}
              </p>
            </Transition>
          </div>
          <div
            v-show="isMixedOpportunity"
            class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-6-at-768 pvd-grid__item--column-span-4-at-960 pvd-grid__item--column-span-4-at-1280">
            <Transition appear name="fade-bottom" mode="out-in">
              <ScreenerCard
                idPrefix="whole"
                :cardStyle="mixedWholeCard.style"
                :cusip="mixedWholeCard.cusip"
                :duration="mixedWholeCard.duration"
                :apy="mixedWholeCard.apy"
                :returnAmount="this.formatDollarAmount(mixedWholeCard.returnAmount)"
                :title="mixedWholeCard.title"
                :showFractionalIcon="false"
                :key="animationTrigger"
                :searchURL="mixedWholeCard.searchURL"
                data-cy="screener-card-mixed-whole" />
            </Transition>
          </div>
          <div
            v-show="isMixedOpportunity"
            class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-6-at-768 pvd-grid__item--column-span-4-at-960 pvd-grid__item--column-span-4-at-1280">
            <Transition appear name="fade-bottom" mode="out-in">
              <ScreenerCard
                idPrefix="fractional"
                :cardStyle="mixedFractionalCard.style"
                :cusip="mixedFractionalCard.cusip"
                :duration="mixedFractionalCard.duration"
                :apy="mixedFractionalCard.apy"
                :returnAmount="formatDollarAmount(mixedFractionalCard.returnAmount)"
                :title="mixedFractionalCard.title"
                :showFractionalIcon="true"
                :key="animationTrigger"
                :searchURL="mixedFractionalCard.searchURL"
                data-cy="screener-card-mixed-fractional" />
            </Transition>
          </div>
          <div
            v-show="isMixedOpportunity"
            data-cy="bottom-bullets-container"
            class="pvd-grid__item pvd-grid__item--column-span-12 pvd-grid__item--column-span-12-at-768 pvd-grid__item--column-span-4-at-960 pvd-grid__item--column-span-4-at-1280">
            <Transition appear name="considerations-fade-bottom" mode="out-in">
              <ul class="side-bullets" :key="animationTrigger">
                <li v-html="considerations"></li>
                <li v-html="combinedReturn"></li>
                <li v-html="earnMore"></li>
                <li v-html="twoTransactions"></li>
                <li v-if="mixedWholeCard.maturityDate == mixedFractionalCard.maturityDate" v-html="bothDates"></li>
                <li v-if="mixedWholeCard.maturityDate != mixedFractionalCard.maturityDate" v-html="fractionalDate"></li>
                <li v-if="mixedWholeCard.maturityDate != mixedFractionalCard.maturityDate" v-html="wholeDate"></li>
                <li v-html="mayBeCallable"></li>
              </ul>
            </Transition>
          </div>
        </div>

        <div class="info-links-section">
          <pvd-link pvd-id="screener_link1" :pvd-href="cdSearchURL" pvd-new-window>{{
            clientConstants.SCREENER_OFFERINGS_LINK
          }}</pvd-link>
          <span v-if="isDesktopWidth" aria-hidden="true">|</span>
          <pvd-link id="screener-link-2" data-cy="screener_link2" pvd-trigger @click="openScreenerModal()">
            {{ clientConstants.SCREENER_SELECT_LINK }}
          </pvd-link>
        </div>
        <ScreenerModal ref="screenerModal" />
      </div>
      <hr aria-hidden="true" />
      <div class="disclosure-section">
        <div class="disclosure-link">
          <pvd-link pvd-id="view-important-info-link" :pvd-href="fiCommonRiskUrl" pvd-new-window>{{
            clientConstants.SCREENER_DISCLOSURE_LINK
          }}</pvd-link>
        </div>
        <div class="contact-us">
          <pvd-button
            pvd-id="contact-us-button"
            :pvd-href="contactUsUrl"
            pvd-new-window="true"
            pvd-variant="secondary"
            >{{ clientConstants.CONTACT_US }}</pvd-button
          >
        </div>
      </div>
      <div class="disclosure-section">
        <pvd-footnotes>
          <pvd-footnote>
            <pvd-microcopy>
              {{ clientConstants.SCREENER_DISCLOSURE_ASTERICK }}
            </pvd-microcopy>
          </pvd-footnote>
          <br />
          <pvd-footnote v-for="disclosure in disclosures" :key="disclosure.text">
            <pvd-microcopy v-html="disclosure.text" data-cy="disclosure"></pvd-microcopy>
            <br />
          </pvd-footnote>
        </pvd-footnotes>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import BreadCrumbs from './BreadCrumbs.vue';
import { ClientConstants, Duration, ScreenerCardStyle } from './../client.constants';
import FractionalCdSlider from './../components/FractionalCdSlider.vue';
import store from './../store/store';
import ScreenerCard from './../components/ScreenerCard.vue';
import FdicModal from './FdicModal.vue';
import ScreenerModal from './ScreenerModal.vue';
import { CDDetail } from './../models/fractional-cd-model';
import { Utils } from './../utils/Utils';
import { ContentComponentsModel, ContentDisclosuresModel } from './../models/content-model';

declare let app: any;

interface CardInfo {
  style: ScreenerCardStyle;
  cusip?: string;
  duration?: Duration;
  apy?: string;
  returnAmount?: number;
  title?: string;
  maturityDate?: string;
  searchURL?: string;
}

export default defineComponent({
  name: 'GuidedScreener',
  components: { BreadCrumbs, FractionalCdSlider, ScreenerCard, FdicModal, ScreenerModal },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.disclosures = this.contentDetailList[5]?.disclosures?.slice(3) ?? [];
    try {
      Utils.trackingFunction(this.clientConstants.SEARCH_PAGE);
    } catch (err) {
      console.log('Error In track analytics ' + err);
    }
  },
  unmounted() {
    window.removeEventListener('resize', this.onResize);
  },
  computed: {
    isDesktopWidth() {
      return this.windowWidth > ClientConstants.TABLET_MAX;
    },
    offerings() {
      return this.store.offeringsModel.offerings;
    },
    formattedAmount() {
      return this.investmentAmount.toFixed(0);
    },
    timestampMessage() {
      const timestamp = Utils.formatTimestamp(store.offeringsModel.timestamp);
      return timestamp == '' ? '' : this.clientConstants.GS_TIMESTAMP_MESSAGE.replace('{{ timestamp }}', timestamp);
    },
    currentDuration() {
      return Object.values(Duration)[this.durationIndex];
    },
    cardIndex() {
      return this.offerings.findIndex(off => off.termToMaturity == this.currentDuration);
    },

    // offerings for duration
    fractionalCD() {
      return this.offerings[this.cardIndex].fractionalCD;
    },
    wholeCD() {
      return this.offerings[this.cardIndex].wholeCD;
    },

    // possible cards
    topCard() {
      return this.cardInfo(this.cardIndex, Number(this.investmentAmount));
    },
    mixedWholeCard() {
      return this.cardInfo(this.cardIndex, this.wholePortion);
    },
    mixedFractionalCard() {
      return this.cardInfo(this.cardIndex, this.fractionalPortion);
    },

    // booleans
    isFractionalInvestmentAmount() {
      return this.investmentAmount < 1000;
    },
    isWholeInvestmentAmount() {
      return this.investmentAmount > 0 && this.investmentAmount % 1000 == 0;
    },
    isMixedInvestmentAmount() {
      return this.investmentAmount > 1000 && this.investmentAmount % 1000 != 0;
    },
    isFractionalCDAvailable() {
      return this.fractionalCD != undefined;
    },
    isWholeCDAvailable() {
      return this.wholeCD != undefined;
    },
    isWholeRateBetter() {
      const wholeRate = this.wholeCD?.rate ?? 0;
      const fractionalRate = this.fractionalCD?.rate ?? 0;
      return wholeRate > fractionalRate;
    },
    isMixedOpportunity() {
      return this.isMixedInvestmentAmount && this.isFractionalCDAvailable && this.isWholeRateBetter;
    },
    isOptionAvailable() {
      const needFractionalCD = this.isFractionalInvestmentAmount || this.isMixedInvestmentAmount;
      return (needFractionalCD && this.isFractionalCDAvailable) || (!needFractionalCD && this.isWholeCDAvailable);
    },
    isIncreaseOpportunity() {
      return (this.isFractionalInvestmentAmount || this.isMixedInvestmentAmount) && this.isWholeCDAvailable;
    },
    isDecreaseOpportunity() {
      return this.isMixedInvestmentAmount && this.isWholeCDAvailable;
    },
    isFractionalToWholeOpportunity() {
      return this.isFractionalInvestmentAmount && this.isFractionalCDAvailable && this.isWholeRateBetter;
    },
    isTopCardFractionalCD() {
      return (
        this.topCard.style !== ScreenerCardStyle.UNAVAILABLE_CARD && this.topCard.cusip === this.fractionalCD?.cusip
      );
    },
    isTopCardWholeCD() {
      return (
        this.topCard.style !== ScreenerCardStyle.UNAVAILABLE_CARD && this.topCard.cusip !== this.fractionalCD?.cusip
      );
    },

    // calculations
    fractionalPortion() {
      return this.investmentAmount % 1000;
    },
    wholePortion() {
      return Math.trunc(this.investmentAmount / 1000) * 1000;
    },
    mixedReturnAmount() {
      const wholeReturn = this.mixedWholeCard.returnAmount ?? 0;
      const fractionalReturn = this.mixedFractionalCard.returnAmount ?? 0;
      return wholeReturn + fractionalReturn;
    },
    mixedReturnAdvantage() {
      const allFractionalReturn = this.topCard.returnAmount ?? 0;
      return this.mixedReturnAmount - allFractionalReturn;
    },

    // card section strings
    cardSectionTitle() {
      if (!this.isOptionAvailable) {
        return this.clientConstants.SCREENER_NO_CDS;
      }

      let text = this.clientConstants.SCREENER_ONE_OPTION;
      if (this.isMixedOpportunity) {
        text = this.clientConstants.SCREENER_TWO_OPTIONS;
      }

      const formattedInvestmentAmount = this.formatDollarAmount(Number(this.investmentAmount), 0);
      text = text.replace('{{ amount }}', formattedInvestmentAmount);
      text = text.replace('{{ duration }}', this.currentDuration);
      return text;
    },
    option1Text() {
      if (!this.isOptionAvailable) {
        return '';
      }
      if (this.isFractionalInvestmentAmount) {
        return this.clientConstants.SCREENER_OPTION_FRACTIONAL;
      }
      if (this.isWholeInvestmentAmount) {
        return this.clientConstants.SCREENER_OPTION_WHOLE;
      }
      // mixed amount, both CDs available, whole amount is better than fractional
      if (this.isMixedOpportunity) {
        return this.clientConstants.SCREENER_OPTION1;
      }
      // mixed amount, but mixed CDs either not available or not optimal
      return this.clientConstants.SCREENER_OPTION_FRACTIONAL;
    },
    option2Text() {
      return this.clientConstants.SCREENER_OPTION2;
    },

    // Considerations
    considerations() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_TITLE;
    },
    totalReturn() {
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        this.topCard.returnAmount
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      const text = this.clientConstants.SCREENER_CONSIDERATIONS_RETURN.replace('{{ return }}', replacement);
      return this.isTopCardFractionalCD ? text : text + '<sup>*</sup>';
    },
    oneTransaction() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_ONE_TRANSACTION;
    },
    oneDate() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_ONE_DATE.replace(
        '{{ date }}',
        this.formatDate(this.topCard.maturityDate)
      );
    },
    combinedReturn() {
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        this.mixedReturnAmount
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_CONSIDERATIONS_COMBINED_RETURN.replace('{{ return }}', replacement);
    },
    earnMore() {
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        this.mixedReturnAdvantage
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_CONSIDERATIONS_EARN_MORE.replace('{{ advantage }}', replacement);
    },
    twoTransactions() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_TWO_TRANSACTIONS;
    },
    bothDates() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_BOTH_MATURITY_DATE.replace(
        '{{ date }}',
        this.formatDate(this.mixedWholeCard.maturityDate)
      );
    },
    fractionalDate() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_FRACTIONAL_MATURITY_DATE.replace(
        '{{ date }}',
        this.formatDate(this.mixedFractionalCard.maturityDate)
      );
    },
    wholeDate() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_WHOLE_MATURITY_DATE.replace(
        '{{ date }}',
        this.formatDate(this.mixedWholeCard.maturityDate)
      );
    },
    mayBeCallable() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_MAY_BE_CALLABLE;
    },

    // Search tips
    searchTips() {
      return this.clientConstants.SCREENER_SEARCH_TIPS_TITLE;
    },
    increaseInvestmentTip() {
      const increase = 1000 - this.fractionalPortion;
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        increase,
        0
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_SEARCH_TIPS_INCREASE.replace('{{ amount }}', replacement);
    },
    decreaseInvestmentTip() {
      const decrease = this.fractionalPortion;
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        decrease,
        0
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_SEARCH_TIPS_DECREASE.replace('{{ amount }}', replacement);
    },
    fractionalToWholeTip() {
      const increase = 1000 - this.fractionalPortion;
      const apy = (this.wholeCD?.rate ?? 0).toFixed(2) + '%';
      const replacement1 = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        increase,
        0
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      const replacement2 = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${apy}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_SEARCH_TIPS_FRACTIONAL_TO_WHOLE.replace(
        '{{ amount }}',
        replacement1
      ).replace('{{ apy }}', replacement2);
    },
    changeDurationTip() {
      return this.clientConstants.SCREENER_SEARCH_TIPS_DURATION;
    },
    contentDetailList() {
      return store.contentItems[0]?.components.map((comp: ContentComponentsModel) => comp.contentDetail) ?? [];
    }
  },
  created() {
    if (this.$route !== undefined && this.$route.query !== undefined) {
      if ('amount' in this.$route.query) {
        this.investmentAmount = Number(this.$route.query.amount);
        this.amountValueInvalid = this.checkAmountInvalid(this.investmentAmount);
        this.amountMaxExceeded = Utils.formatNumber(this.investmentAmount) > 9999900;
      }
      if ('duration' in this.$route.query) {
        this.durationIndex = Number(this.$route.query.duration);
      }
    }
  },
  data: () => {
    return {
      linkList: [
        { text: ClientConstants.HOME, href: `${app?.hostUrl}`, id: 'gs-breadcrumb-home' },
        { text: ClientConstants.BREADCRUMB_ARROWS },
        {
          text: ClientConstants.RESEARCH,
          href: `${app?.hostUrl}${app?.fiBondsOverviewPath}`,
          id: 'gs-breadcrumb-fi-bonds'
        },
        { text: ClientConstants.BREADCRUMB_ARROWS },
        { text: ClientConstants.FRACTIONAL_CDS, href: `${app?.educationPagePath}`, id: 'gs-breadcrumb-edu' }
      ],
      amountValueInvalid: false,
      amountMaxExceeded: false,
      clientConstants: ClientConstants,
      openContactUs: "window.open('" + ClientConstants.CONTACT_US_URL + "', '_blank');",
      store: reactive(store),
      investmentAmount: 0,
      durationIndex: 0,
      fiCommonRiskUrl: app ? `${app.hostUrl}${app.fiCommonRiskPath}` : '',
      cdSearchURL: app ? `${app.fixedIncomeHost}${app.fiLandingCdLadders}` : '',
      contactUsUrl: app ? `${app.hostUrl}${app.contactUsPath}` : '',
      animationTrigger: 0,
      windowWidth: window.innerWidth,
      disclosures: [] as ContentDisclosuresModel[]
    };
  },
  methods: {
    onResize: function () {
      this.windowWidth = window.innerWidth;
    },
    checkAmountInvalid(amount) {
      const passedNumber = Utils.formatNumber(amount);
      return passedNumber == undefined || isNaN(passedNumber) || passedNumber < 100 || passedNumber % 100 != 0;
    },
    amountChange(event) {
      this.amountValueInvalid = this.checkAmountInvalid(event.target.value);
      this.amountMaxExceeded = Utils.formatNumber(event.target.value) > 9999900;
      if (!this.amountValueInvalid && !this.amountMaxExceeded) {
        this.investmentAmount = Utils.formatNumber(event.target.value);
        this.animationTrigger++;
      }
    },
    durationChange(event) {
      if (!this.amountValueInvalid && !this.amountMaxExceeded) {
        this.animationTrigger++;
        this.durationIndex = Number(event.target.value);
      }
    },
    openModal() {
      (this.$refs.fdicModal as typeof FdicModal).openFdicModal();
    },
    openScreenerModal() {
      (this.$refs.screenerModal as typeof ScreenerModal).openScreenerModal();
    },
    cardInfo(index: number, cardAmount: number): CardInfo {
      // this should never happen
      if (index < 0 || index >= this.offerings.length) {
        return { style: ScreenerCardStyle.UNAVAILABLE_CARD, duration: undefined, title: undefined };
      }

      const cd = this.chooseCD(cardAmount, this.offerings[index].fractionalCD, this.offerings[index].wholeCD);
      const cardAmountIsFractional = cardAmount % 1000 != 0;

      // unavailable card if no CD matches the selected amount
      if (cd === undefined) {
        const duration = Object.values(Duration)[index];
        let titleText = '';

        if (!this.isWholeCDAvailable && !this.isFractionalCDAvailable) {
          titleText = this.clientConstants.SCREENER_CARD_NO_CDS;
        } else if (cardAmountIsFractional) {
          titleText = this.clientConstants.SCREENER_CARD_NO_FRACTIONAL_CDS;
        }

        const title = titleText.replace('{{ duration }}', duration);
        const searchURL = this.cdSearchURL;
        return { style: ScreenerCardStyle.UNAVAILABLE_CARD, duration, title, searchURL };
      }

      // buy card
      const style = ScreenerCardStyle.BUY_CARD;
      const cusip = cd.cusip;
      const duration = Object.values(Duration)[index];
      const apy = cd.rate.toFixed(2) + '%';
      const returnAmount = Utils.calculateReturnAmount(cardAmount, cd.rate, cd.datedDate, cd.maturityDate);
      const formattedCardAmount = this.formatDollarAmount(cardAmount, 0);
      const title = cardAmountIsFractional
        ? this.clientConstants.SCREENER_CARD_FRACTIONAL_AMOUNT.replace('{{ amount }}', formattedCardAmount)
        : this.clientConstants.SCREENER_CARD_WHOLE_AMOUNT.replace('{{ amount }}', formattedCardAmount);
      const maturityDate = cd.maturityDate;
      const searchURL = cardAmountIsFractional
        ? Utils.buildFractionalCDSearchURL(this.cdSearchURL, cusip)
        : Utils.buildWholeCDSearchURL(
            this.cdSearchURL,
            this.offerings[index].minMaturityDate as Date,
            this.offerings[index].maxMaturityDate as Date
          );
      return { style, cusip, duration, apy, returnAmount, title, maturityDate, searchURL };
    },
    chooseCD(amount: number, fractionalCD?: CDDetail, wholeCD?: CDDetail): CDDetail | undefined {
      let selectedCD = fractionalCD;
      const fractionalRate = fractionalCD?.rate;
      const wholeRate = wholeCD?.rate;
      const isWholeAmount = amount > 0 && amount % 1000 == 0;
      if (isWholeAmount && wholeRate !== undefined && (fractionalRate === undefined || fractionalRate <= wholeRate)) {
        selectedCD = wholeCD;
      }
      return selectedCD;
    },
    formatDollarAmount(amount?: number, decimalPlaces = 2): string {
      if (!amount || typeof amount !== 'number') {
        return '$0';
      }
      return (
        '$' +
        Number(amount.toFixed(decimalPlaces)).toLocaleString('en-US', {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        })
      );
    },
    formatDate(dateString: string | undefined) {
      return Utils.formatMaturityDate(dateString);
    }
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import '@fmr-pr101742/providence-visual-style/styles/visual-style.scss';

// These colors match Figma but are not defined in Providence
$medium-gray: #f7f7f7;

.title-section {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
.amount-section {
  padding-top: 1rem;
  width: 21rem;
  margin-bottom: 0;
  @media only screen and (min-width: $desktop_max) {
    margin-bottom: 1em;
  }
  @media only screen and (max-width: 350px) {
    width: 100%;
  }
}

.regular-font {
  font-weight: $pvd-font-weight-regular !important;
  @include pvd-text-large();
}

.fdic-link {
  margin-top: 0.5rem;
  @include pvd-text-small;
}
.gs-timestamp {
  padding-top: 1em;

  @media only screen and (min-width: $mobile_max) {
    padding-top: 1em;
  }
  @media only screen and (min-width: $tablet_max) {
    padding-top: 0;
  }
  @media only screen and (min-width: $desktop_mid) {
    padding-top: 4em;
  }
  @media only screen and (min-width: $desktop_max) {
    padding-top: 4em;
  }
}

#time-period-slider-component {
  width: 90%;
  max-width: 20em;
  float: left;

  @media only screen and (min-width: $mobile_max) {
    float: left;
  }
  @media only screen and (min-width: $tablet_max) {
    float: left;
  }
  @media only screen and (min-width: $desktop_mid) {
    float: right;
  }
  @media only screen and (min-width: $desktop_max) {
    float: left;
  }
}

.full-width {
  width: 100;
  margin-left: 0;
  margin-right: 0;
}
div.top-half {
  padding-bottom: 0.25rem;
}
div.bottom-half {
  background-color: $medium-gray;
  overflow: hidden;
  margin-bottom: -18px; // removes white band between screener and footer
}
div.card-section {
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;
}
div.disclosure-section {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 1.25rem 2rem 0rem 2rem;
  overflow: hidden; // required due to float styling used inside div
}

div.footnotes-section {
  padding: 0 2rem 3.125rem 2rem;
}

pvd-link {
  text-align: left;
  padding-right: 1rem;
}
.contact-us {
  @media only screen and (min-width: $mobile_max) {
    float: right;
  }
}
.disclosure-link {
  @media only screen and (min-width: $mobile_max) {
    float: left;
  }
}

ul.side-bullets {
  list-style-type: none;
  @include pvd-text-medium;
  font-weight: 400;
  text-align: left;
  margin: 0 0 0 1rem;
  padding: 0;
  width: 100%;
}
ul.side-bullets li {
  padding: 0.6rem 0;
  border-bottom: 1px solid #ccc;
  width: 85%;
}
ul.side-bullets li:first-child {
  font-weight: 700;
}
ul.side-bullets li:last-child::after {
  display: none;
}
ul.top-margin li:first-child {
  margin-top: 1rem;
}
.card-section-title {
  float: left;
  padding-left: 0.5rem;
  margin: 0;
  @include pvd-heading-level-4();
  font-weight: 400;
}
.option-text {
  float: left;
  padding-left: 0.5rem;
  margin: 0;
  @include pvd-heading-level-5();
  font-weight: 400;
}
.option-2 {
  margin-top: 2rem;
}
.info-links-section {
  @media only screen and (max-width: $tablet_max) {
    display: flex;
    flex-direction: column;
  }
}
.info-links-section pvd-link {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  padding-bottom: 1.75rem;
  text-align: center;
}
.disclosure-section pvd-link {
  padding-bottom: 1.75rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.considerations-fade-enter-active,
.considerations-fade-leave-active {
  transition: opacity 1s ease;
}

.considerations-fade-enter-from,
.considerations-fade-leave-to {
  opacity: 0;
}

.fade-bottom-enter-active,
.fade-bottom-leave-active {
  transition: opacity 1.5s ease;
}

.fade-bottom-enter-from,
.fade-bottom-leave-to {
  opacity: 0;
}

.considerations-fade-bottom-enter-active,
.considerations-fade-bottom-leave-active {
  transition: opacity 2s ease;
}

.considerations-fade-bottom-enter-from,
.considerations-fade-bottom-leave-to {
  opacity: 0;
}

#amount-label {
  font-size: 1.25rem;
}
</style>
